import React from 'react';
import { Box, Container, Typography, Card, CardContent, Grid, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import AssessmentIcon from '@mui/icons-material/Assessment';

const Achievements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const achievements = [
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: '15-20% Market Share Growth',
      description: [
        'Spearheaded transformative sales and marketing strategies that revolutionized market positioning.',
        'Achieved unprecedented market penetration through innovative approaches and data-driven insights.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
      title: 'Best Sales Manager Award',
      description: [
        'Recognized as the region\'s top-performing sales leader for exceptional achievements in FY 2015-16.',
        'This prestigious honor reflects outstanding dedication, strategic vision, and remarkable results.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40 }} />,
      title: '25% Increase in Business Leads',
      description: [
        'Orchestrated spectacular international trade exhibitions that transformed brand visibility globally.',
        'Generated a surge of high-quality leads through strategic networking and innovative presentations.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      title: '12% Debt Reduction',
      description: [
        'Engineered a revolutionary credit management system that streamlined collection processes.',
        'Significantly reduced outstanding debt while maintaining strong customer relationships.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: '20% Sales Performance Boost',
      description: [
        'Crafted comprehensive sales team development programs that empowered professionals to excel.',
        'Transformed team capabilities through innovative training methodologies and mentorship.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      title: '15% Customer Satisfaction Improvement',
      description: [
        'Established a world-class feedback and resolution framework that revolutionized customer experience.',
        'Elevated customer satisfaction to unprecedented levels through proactive communication strategies.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: '10-15% Year-over-Year Growth',
      description: [
        'Masterminded Key Account Management strategies that forged unbreakable partnerships with leaders.',
        'Secured long-term contracts through personalized relationship building and strategic value creation.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      title: '25% Market Demand Increase',
      description: [
        'Conducted groundbreaking market research initiatives that uncovered hidden opportunities.',
        'Informed product development strategies that perfectly aligned with market needs and drove demand.',
      ],
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: 'linear-gradient(90deg, #0968E5, #091970)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 6 },
            fontWeight: 700,
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
          }}
        >
          Notable Achievements
        </Typography>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Grid container spacing={3}>
            {achievements.map((achievement, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: { xs: '280px', sm: '300px', md: '320px' },
                      borderRadius: 4,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        height: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: { xs: 65, sm: 70, md: 80 },
                            height: { xs: 65, sm: 70, md: 80 },
                            borderRadius: '50%',
                            background: achievement.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            mb: { xs: 1.5, md: 2 },
                            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                            '& svg': {
                              display: 'block',
                              margin: 'auto',
                              fontSize: { xs: 32, sm: 36, md: 40 },
                            },
                          }}
                        >
                          {achievement.icon}
                        </Box>
                        <Typography
                          variant={isMobile ? 'h6' : 'h6'}
                          sx={{
                            fontWeight: 700,
                            mb: { xs: 1.5, md: 2 },
                            color: '#333',
                            fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem', lg: '1.1rem' },
                          }}
                        >
                          {achievement.title}
                        </Typography>
                      </Box>
                      <Box>
                        {achievement.description.map((line, idx) => (
                          <Typography
                            key={idx}
                            variant={isMobile ? 'body2' : 'body2'}
                            sx={{
                              color: '#666',
                              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem' },
                              lineHeight: { xs: 1.5, md: 1.6 },
                              mb: idx < achievement.description.length - 1 ? { xs: 0.75, md: 1 } : 0,
                            }}
                          >
                            {line}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Achievements;

